import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, PhotoIcon, XMarkIcon, CloudArrowUpIcon } from '@heroicons/react/24/outline';
import { isTokenValid, clearAdminAuth } from '../utils/auth';

interface VehicleFormData {
  make: string;
  model: string;
  year: number | string;
  body_style: string;
  engine: string;
  transmission: string;
  drive_type: string;
  fuel_type: string;
  exterior_color: string;
  interior_color: string;
  odometer: number | string;
  odometer_unit: string;
  price: number | string;
  currency: string;
  title: string;
  description: string;
  features: string[];
  location: string;
  vehicle_type: string;  // "available" or "canada_dealer"
  estimated_delivery_days: number | string;
  is_available: boolean;
  is_featured: boolean;
}

interface ImageData {
  id?: number;
  image_url: string;
  image_type: string;
  caption: string;
}

const AdminVehicleForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id && id !== 'new';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState<VehicleFormData>({
    make: '',
    model: '',
    year: '',
    body_style: '',
    engine: '',
    transmission: '',
    drive_type: '',
    fuel_type: '',
    exterior_color: '',
    interior_color: '',
    odometer: '',
    odometer_unit: 'km',
    price: '',
    currency: 'BGN',
    title: '',
    description: '',
    features: [],
    location: '',
    vehicle_type: 'available',  // Default to "in Bulgaria"
    estimated_delivery_days: 45,
    is_available: true,
    is_featured: true
  });

  const [images, setImages] = useState<ImageData[]>([]);
  const [newFeature, setNewFeature] = useState('');
  const [newImageUrl, setNewImageUrl] = useState('');
  const [newImageType, setNewImageType] = useState('exterior');
  const [newImageCaption, setNewImageCaption] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token || !isTokenValid(token)) {
      clearAdminAuth();
      navigate('/admin/login', { replace: true });
      return;
    }

    if (isEditMode) {
      fetchVehicle();
    }
  }, [id, isEditMode, navigate]);

  const fetchVehicle = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token || !isTokenValid(token)) {
        clearAdminAuth();
        navigate('/admin/login', { replace: true });
        return;
      }
      const response = await fetch(`/api/v1/featured-inventory/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        clearAdminAuth();
        navigate('/admin/login', { replace: true });
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch vehicle');

      const data = await response.json();
      setFormData({
        make: data.make || '',
        model: data.model || '',
        year: data.year || '',
        body_style: data.body_style || '',
        engine: data.engine || '',
        transmission: data.transmission || '',
        drive_type: data.drive_type || '',
        fuel_type: data.fuel_type || '',
        exterior_color: data.exterior_color || '',
        interior_color: data.interior_color || '',
        odometer: data.odometer || '',
        odometer_unit: data.odometer_unit || 'km',
        price: data.price || '',
        currency: data.currency || 'BGN',
        title: data.title || '',
        description: data.description || '',
        features: data.features || [],
        location: data.location || '',
        vehicle_type: data.vehicle_type || 'available',
        estimated_delivery_days: data.estimated_delivery_days || 45,
        is_available: data.is_available ?? true,
        is_featured: data.is_featured ?? true
      });
      setImages(data.images || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load vehicle');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const token = localStorage.getItem('admin_token');
      if (!token || !isTokenValid(token)) {
        clearAdminAuth();
        navigate('/admin/login', { replace: true });
        return;
      }
      const url = isEditMode 
        ? `/api/v1/featured-inventory/${id}`
        : '/api/v1/featured-inventory/';
      
      const method = isEditMode ? 'PUT' : 'POST';

      // Prepare the data
      const submitData = {
        ...formData,
        year: parseInt(formData.year as string),
        odometer: formData.odometer ? parseInt(formData.odometer as string) : null,
        price: parseFloat(formData.price as string),
        features: formData.features,
        estimated_delivery_days: formData.estimated_delivery_days ? parseInt(formData.estimated_delivery_days as string) : null
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      if (response.status === 401) {
        clearAdminAuth();
        navigate('/admin/login', { replace: true });
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save vehicle');
      }

      const savedVehicle = await response.json();

      // Save only new images (ones without an id)
      const newImages = images.filter((img: any) => !img.id);
      if (newImages.length > 0 && savedVehicle.id) {
        await saveImages(savedVehicle.id, newImages);
      }

      if (isEditMode) {
        setSuccess('Vehicle updated successfully!');
        setTimeout(() => {
          navigate('/admin/dashboard');
        }, 1500);
      } else {
        setSuccess('Vehicle added! Redirecting to upload images...');
        setTimeout(() => {
          navigate(`/admin/featured-inventory/${savedVehicle.id}`);
        }, 1500);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save vehicle');
    } finally {
      setLoading(false);
    }
  };

  const saveImages = async (vehicleId: number, imagesToSave: ImageData[]) => {
    const token = localStorage.getItem('admin_token');
    if (!token || !isTokenValid(token)) {
      clearAdminAuth();
      navigate('/admin/login', { replace: true });
      return;
    }
    for (let i = 0; i < imagesToSave.length; i++) {
      const image = imagesToSave[i];
      try {
        // Send as form data with query parameters
        const params = new URLSearchParams({
          image_url: image.image_url,
          image_type: image.image_type || 'main',
          image_order: i.toString(),
          ...(image.caption && { caption: image.caption })
        });
        
        const response = await fetch(`/api/v1/featured-inventory/${vehicleId}/images/?${params.toString()}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          console.error('Failed to save image:', await response.text());
        }
      } catch (err) {
        console.error('Failed to save image:', err);
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    });
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData({
        ...formData,
        features: [...formData.features, newFeature.trim()]
      });
      setNewFeature('');
    }
  };

  const removeFeature = (index: number) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index)
    });
  };

  const addImage = () => {
    if (newImageUrl.trim()) {
      setImages([...images, {
        image_url: newImageUrl.trim(),
        image_type: newImageType,
        caption: newImageCaption.trim()
      }]);
      setNewImageUrl('');
      setNewImageCaption('');
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // For new vehicles, we need to save the vehicle first
    if (!isEditMode) {
      setError('Моля, първо запазете автомобила, след което добавете снимки.');
      return;
    }

    const token = localStorage.getItem('admin_token');
    if (!token || !isTokenValid(token)) {
      clearAdminAuth();
      navigate('/admin/login', { replace: true });
      return;
    }

    setUploadingImage(true);
    setError('');

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append('file', file);
      formData.append('image_type', newImageType);
      formData.append('image_order', String(images.length + i));
      if (newImageCaption) {
        formData.append('caption', newImageCaption);
      }

      try {
        const response = await fetch(`/api/v1/featured-inventory/${id}/upload-image/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to upload image');
        }

        const updatedVehicle = await response.json();
        setImages(updatedVehicle.images || []);
        setSuccess(`Снимка ${i + 1} качена успешно!`);
      } catch (err: any) {
        setError(err.message || 'Грешка при качване на снимка');
      }
    }

    setUploadingImage(false);
    setNewImageCaption('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeImage = async (index: number) => {
    const token = localStorage.getItem('admin_token');
    if (!token || !isTokenValid(token)) {
      clearAdminAuth();
      navigate('/admin/login', { replace: true });
      return;
    }
    const imageToRemove = images[index];
    
    // If the image has an id, it's an existing image - delete from backend
    if (imageToRemove.id && isEditMode) {
      try {
        await fetch(`/api/v1/featured-inventory/${id}/images/${imageToRemove.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      } catch (err) {
        console.error('Failed to delete image:', err);
      }
    }
    
    // Remove from local state
    setImages(images.filter((_, i) => i !== index));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="mr-4 p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditMode ? 'Edit Vehicle' : 'Add New Vehicle'}
            </h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Messages */}
        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
        {success && (
          <div className="mb-6 bg-green-50 border-l-4 border-green-500 p-4 rounded">
            <p className="text-sm text-green-700">{success}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Vehicle Type Selection */}
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Тип на автомобила</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label 
                className={`flex items-center p-4 rounded-lg cursor-pointer transition-all ${
                  formData.vehicle_type === 'available' 
                    ? 'bg-white shadow-lg' 
                    : 'bg-white/20 hover:bg-white/30'
                }`}
              >
                <input
                  type="radio"
                  name="vehicle_type"
                  value="available"
                  checked={formData.vehicle_type === 'available'}
                  onChange={handleChange}
                  className="w-5 h-5 text-primary-600"
                />
                <div className="ml-3">
                  <span className={`font-semibold ${formData.vehicle_type === 'available' ? 'text-gray-900' : 'text-white'}`}>
                    🇧🇬 Налични автомобили
                  </span>
                  <p className={`text-sm ${formData.vehicle_type === 'available' ? 'text-gray-600' : 'text-white/80'}`}>
                    Вече в България, готови за продажба
                  </p>
                </div>
              </label>

              <label 
                className={`flex items-center p-4 rounded-lg cursor-pointer transition-all ${
                  formData.vehicle_type === 'canada_dealer' 
                    ? 'bg-white shadow-lg' 
                    : 'bg-white/20 hover:bg-white/30'
                }`}
              >
                <input
                  type="radio"
                  name="vehicle_type"
                  value="canada_dealer"
                  checked={formData.vehicle_type === 'canada_dealer'}
                  onChange={handleChange}
                  className="w-5 h-5 text-primary-600"
                />
                <div className="ml-3">
                  <span className={`font-semibold ${formData.vehicle_type === 'canada_dealer' ? 'text-gray-900' : 'text-white'}`}>
                    🇨🇦 Дилърски коли от Канада
                  </span>
                  <p className={`text-sm ${formData.vehicle_type === 'canada_dealer' ? 'text-gray-600' : 'text-white/80'}`}>
                    Внос от Канада, цена до България
                  </p>
                </div>
              </label>
            </div>

            {/* Show delivery time for Canada dealer vehicles */}
            {formData.vehicle_type === 'canada_dealer' && (
              <div className="mt-4 p-4 bg-white/10 rounded-lg">
                <label className="block text-sm font-medium text-white mb-2">
                  Очаквано време за доставка (дни)
                </label>
                <input
                  type="number"
                  name="estimated_delivery_days"
                  value={formData.estimated_delivery_days}
                  onChange={handleChange}
                  min="1"
                  className="w-full md:w-48 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="45"
                />
              </div>
            )}
          </div>

          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Make <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="make"
                  required
                  value={formData.make}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., Mercedes-Benz"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Model <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="model"
                  required
                  value={formData.model}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., S-Class"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Year <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="year"
                  required
                  min="1990"
                  max={new Date().getFullYear() + 1}
                  value={formData.year}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., 2020"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Body Style
                </label>
                <input
                  type="text"
                  name="body_style"
                  value={formData.body_style}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., Sedan, SUV, Coupe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Custom title (optional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., Sofia, Bulgaria"
                />
              </div>
            </div>
          </div>

          {/* Specifications */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Specifications</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Engine
                </label>
                <input
                  type="text"
                  name="engine"
                  value={formData.engine}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., 3.0L V6 Turbo"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transmission
                </label>
                <select
                  name="transmission"
                  value={formData.transmission}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Select...</option>
                  <option value="Automatic">Automatic</option>
                  <option value="Manual">Manual</option>
                  <option value="CVT">CVT</option>
                  <option value="Semi-Automatic">Semi-Automatic</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Drive Type
                </label>
                <select
                  name="drive_type"
                  value={formData.drive_type}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Select...</option>
                  <option value="FWD">FWD (Front-Wheel Drive)</option>
                  <option value="RWD">RWD (Rear-Wheel Drive)</option>
                  <option value="AWD">AWD (All-Wheel Drive)</option>
                  <option value="4WD">4WD (Four-Wheel Drive)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fuel Type
                </label>
                <select
                  name="fuel_type"
                  value={formData.fuel_type}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Select...</option>
                  <option value="Gasoline">Gasoline</option>
                  <option value="Diesel">Diesel</option>
                  <option value="Hybrid">Hybrid</option>
                  <option value="Electric">Electric</option>
                  <option value="Plug-in Hybrid">Plug-in Hybrid</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Exterior Color
                </label>
                <input
                  type="text"
                  name="exterior_color"
                  value={formData.exterior_color}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., Black, White, Silver"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Interior Color
                </label>
                <input
                  type="text"
                  name="interior_color"
                  value={formData.interior_color}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., Black, Beige, Gray"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Odometer
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    name="odometer"
                    value={formData.odometer}
                    onChange={handleChange}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="e.g., 50000"
                  />
                  <select
                    name="odometer_unit"
                    value={formData.odometer_unit}
                    onChange={handleChange}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="km">km</option>
                    <option value="mi">miles</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Pricing</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="price"
                  required
                  min="0"
                  step="0.01"
                  value={formData.price}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., 25000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Currency
                </label>
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="EUR">EUR (€)</option>
                  <option value="USD">USD ($)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Description</h2>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={6}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Detailed description of the vehicle..."
            />
          </div>

          {/* Features */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Features & Highlights</h2>
            <div className="space-y-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., Leather seats, Navigation system"
                />
                <button
                  type="button"
                  onClick={addFeature}
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Add
                </button>
              </div>

              {formData.features.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.features.map((feature, index) => (
                    <div
                      key={index}
                      className="inline-flex items-center px-3 py-1 bg-primary-100 text-primary-700 rounded-full"
                    >
                      <span className="text-sm">{feature}</span>
                      <button
                        type="button"
                        onClick={() => removeFeature(index)}
                        className="ml-2 text-primary-600 hover:text-primary-800"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Images */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <PhotoIcon className="w-5 h-5 mr-2" />
              Снимки
            </h2>
            <div className="space-y-6">
              {/* File Upload Section */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-500 transition-colors">
                <CloudArrowUpIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600 mb-2">
                  {isEditMode ? 'Качете снимки от вашия компютър' : 'Първо запазете автомобила, след което качете снимки'}
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  multiple
                  onChange={handleFileUpload}
                  disabled={!isEditMode || uploadingImage}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className={`inline-flex items-center px-6 py-3 rounded-lg transition-colors cursor-pointer ${
                    isEditMode && !uploadingImage
                      ? 'bg-primary-600 text-white hover:bg-primary-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <CloudArrowUpIcon className="w-5 h-5 mr-2" />
                  {uploadingImage ? 'Качване...' : 'Качете снимки'}
                </label>
                <p className="text-xs text-gray-500 mt-2">JPG, PNG, WebP или GIF. Можете да изберете няколко снимки.</p>
              </div>

              {/* OR divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-white px-4 text-gray-500">или добавете чрез URL</span>
                </div>
              </div>

              {/* URL Input Section */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <input
                  type="url"
                  value={newImageUrl}
                  onChange={(e) => setNewImageUrl(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Image URL"
                />
                <select
                  value={newImageType}
                  onChange={(e) => setNewImageType(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="main">Основна</option>
                  <option value="exterior">Екстериор</option>
                  <option value="interior">Интериор</option>
                  <option value="detail">Детайл</option>
                </select>
                <input
                  type="text"
                  value={newImageCaption}
                  onChange={(e) => setNewImageCaption(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Описание (по избор)"
                />
              </div>
              <button
                type="button"
                onClick={addImage}
                className="w-full px-6 py-2 border border-primary-600 text-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
              >
                Add Image
              </button>

              {images.length > 0 && (
                <div className="grid grid-cols-1 gap-4 mt-4">
                  {images.map((image, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                    >
                      <div className="flex items-center space-x-4">
                        <img
                          src={image.image_url}
                          alt={image.caption || 'Vehicle'}
                          className="w-20 h-20 object-cover rounded"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'https://via.placeholder.com/80?text=Image';
                          }}
                        />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{image.image_type}</p>
                          {image.caption && (
                            <p className="text-sm text-gray-600">{image.caption}</p>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <XMarkIcon className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Status */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Статус</h2>
            <div className="space-y-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="is_available"
                  checked={formData.is_available}
                  onChange={handleChange}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  {formData.vehicle_type === 'canada_dealer' 
                    ? 'Наличен за продажба (показва се в /vehicle-dealer)' 
                    : 'Наличен за продажба (показва се в /available)'}
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="is_featured"
                  checked={formData.is_featured}
                  onChange={handleChange}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  {formData.vehicle_type === 'canada_dealer' 
                    ? 'Покажи в carousel "Дилърски коли" на началната страница' 
                    : 'Покажи в carousel "Препоръчани" на началната страница'}
                </span>
              </label>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/admin/dashboard')}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : (isEditMode ? 'Update Vehicle' : 'Add Vehicle')}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default AdminVehicleForm;

